import { DataTable } from "./data-table";
import { useMemo, useState, useRef, useEffect } from "react";
import { ColumnDef, SortingState } from "@tanstack/react-table";
import { certificationStatus } from "../../../constant";
import { ReactComponent as StarIcon } from "../../../assets/icons/star.svg";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { useApp } from "../../../services/app-context";
import { twMerge } from "tailwind-merge";
import { buildReviewUrl, WorkflowStatusCell } from "../admin-certification/CertificationAssignmentList";
import { ReactComponent as FilterIcon } from "../../../assets/icons/sort.svg";
import { getLabel, getUrl } from "../utils/labels";
import { isCertificationOverallSummaryEnabled } from "../../../integrations/posthog/featureFlags";

dayjs.extend(utc);
dayjs.extend(timezone);

function LoadingSkeleton() {
  return (
    <>
      {Array.from({ length: 7 }).map((_, i) => (
        <tr
          key={i}
          className="bg-white [&_td]:py-3 [&_td_div]:rounded-[6px] [&_td]:border-b [&_td]border-b-[#E5E7EB]"
        >
          <td className="pl-3">
            <div className="w-24 h-4 bg-slate-200 animate-pulse transition duration-50" />
          </td>
          <td>
            <div className="w-16 h-4 bg-slate-200 animate-pulse transition duration-50" />
          </td>
          <td>
            <div className="flex items-center justify-center">
              <div className="w-16 h-4 bg-slate-200 animate-pulse transition duration-50 text-center" />
            </div>
          </td>
          <td>
            <div className="flex items-center justify-center">
              <div className="w-16 h-4 bg-slate-200 animate-pulse transition duration-50" />
            </div>
          </td>
          <td className="subscription">
            <div className="flex items-center justify-center">
              <div className="w-16 h-4 bg-slate-200 animate-pulse transition duration-50" />
            </div>
          </td>
        </tr>
      ))}
    </>
  );
}

// Views paginated with a server cursor must omit `filter.sortBy`: re-sorting only
// the pages fetched so far can contradict the server's ordering and make rows
// jump as later pages arrive.
const filterData = (
  data: any[],
  filter: any,
  type: "user-dashboard" | "manager-dashboard" | "manager-user-table" | "admin-dashboard"
) => {
  let filteredData = [...data];
  const isManagerDashboard = type === "manager-dashboard";
  if (filter?.search) {
    filteredData = filteredData.filter((item) => {
      // if manager dashboard then check userName too
      if (isManagerDashboard) {
        const searchTerm = filter.search.toLowerCase();
        const certName =
          item?.certification?.certificationName?.toLowerCase() ?? "";
        const firstName = item?.user?.firstName?.toLowerCase() ?? "";
        const lastName = item?.user?.lastName?.toLowerCase() ?? "";

        return (
          certName.includes(searchTerm) ||
          firstName.includes(searchTerm) ||
          lastName.includes(searchTerm)
        );
      }

      const searchTerm = filter.search.toLowerCase();
      const certName =
        item?.certification?.certificationName?.toLowerCase() ?? "";
      return certName.includes(searchTerm);
    });
  }
  if (filter?.sortBy && filter?.sortOrder) {
    filteredData = filteredData.sort((a, b) => {
      if (filter.sortBy === "createdAt") {
        // date filter
        const aDate = dayjs(a.createdAt).utc().format("YYYY-MM-DD");
        const bDate = dayjs(b.createdAt).utc().format("YYYY-MM-DD");
        const before =
          filter.sortOrder === "desc"
            ? dayjs(bDate).isBefore(aDate)
            : dayjs(aDate).isBefore(bDate);
        return before ? -1 : 1;
      }
      return filter.sortOrder === "desc"
        ? b[filter.sortBy] - a[filter.sortBy]
        : a[filter.sortBy] - b[filter.sortBy];
    });
  }
  return filteredData;
};

export function StatusCell({ status, certificationAssets, className }: { status: string, certificationAssets?: any, className?: string }) {
  const statusData = certificationAssets?.userStatus?.[status] || certificationAssets?.userStatus?.pending;
  return (
    <div
      className={twMerge(`rounded-md w-[102px] h-[37px] flex items-center justify-center font-medium text-[13px]`, className)}
      style={{
        backgroundColor: statusData?.backgroundColor,
        color: statusData?.textColor,
      }}
    >
      {statusData?.label}
    </div>
  );
}

function PraxisScoreCell({ score }: { score: number }) {
  const status = !score ? "N/A" : score < 3 ? "Fail" : "Pass";
  const statusData = certificationStatus[
    status as keyof typeof certificationStatus
  ] || {
    label: "N/A",
    bgColor: "transparent",
    textColor: "#C6C6C6",
  };
  return (
    <div
      className={`rounded-md gap-2 w-[102px] h-[37px] flex items-center justify-center text-[13px]`}
      style={{
        backgroundColor: statusData.bgColor,
        color: statusData.textColor,
      }}
    >
      {score && <StarIcon className="w-4 h-4" />}
      {score || "N/A"}
    </div>
  );
}

function ActionMenu({ row, onStart, onView }: { row: any; onStart: (row: any) => void; onView: (row: any) => void }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const todayUtc = dayjs.utc().format("YYYY-MM-DD");
  const startDate = row.certificationStartDate;
  const dueDate = row.certificationDueDate;
  const startDateOk = startDate != null && todayUtc >= dayjs.utc(startDate).format("YYYY-MM-DD");
  const dueDateOk = dueDate != null && todayUtc <= dayjs.utc(dueDate).format("YYYY-MM-DD");
  const startEnabled = row.canBeTaken === true && startDateOk && dueDateOk;
  const attemptCount = row.attemptCount ?? 0;
  const showOverallSummary = isCertificationOverallSummaryEnabled();
  const viewEnabled = showOverallSummary ? attemptCount > 0 : attemptCount > 1 || (attemptCount === 1 && ["passed", "failed"].includes(row.evaluationStatus));

  const handleToggle = () => {
    if (!open && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPos({ top: rect.bottom + 4, left: rect.right - 100 });
    }
    setOpen((o) => !o);
  };

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  // Close if the table scrolls so the dropdown doesn't float detached
  useEffect(() => {
    if (!open) return;
    const handleScroll = () => setOpen(false);
    window.addEventListener("scroll", handleScroll, true);
    return () => window.removeEventListener("scroll", handleScroll, true);
  }, [open]);

  return (
    <div ref={containerRef} className="relative flex justify-center">
      <button
        ref={triggerRef}
        onClick={handleToggle}
        className="text-xl font-bold text-text2 hover:text-primary3 px-2 tracking-widest leading-none"
      >
        ···
      </button>
      {open && (
        <div
          className="fixed z-50 bg-white border border-gray-200 rounded-md shadow-md py-1 min-w-[100px]"
          style={{ top: pos.top, left: pos.left }}
        >
          <button
            onClick={() => {
              if (!startEnabled) return;
              setOpen(false);
              onStart(row);
            }}
            className={twMerge(
              "w-full text-left px-4 py-2 text-sm text-text2 hover:bg-gray-50",
              !startEnabled && "opacity-50 cursor-not-allowed hover:bg-transparent"
            )}
          >
            Start
          </button>
          <button
            onClick={() => {
              if (!viewEnabled) return;
              setOpen(false);
              onView(row);
            }}
            className={twMerge(
              "w-full text-left px-4 py-2 text-sm text-text2 hover:bg-gray-50",
              !viewEnabled && "opacity-50 cursor-not-allowed hover:bg-transparent"
            )}
          >
            View Insights
          </button>
        </div>
      )}
    </div>
  );
}

const getColumns = (
  { onStart, onView, showOverallSummary, enableStatusSorting = true }: { onStart: (row: any) => void; onView: (row: any) => void; showOverallSummary?: boolean; enableStatusSorting?: boolean },
  certificationAssets?: any,
) => {
  const labels = certificationAssets?.labels || {};
  const commonColumns = [
    {
      accessorKey: "attempts",
      header: ({ column }) => {
        return <div>{getLabel("attempt.plural", labels)}</div>;
      },
      cell: ({ row }) => {
        return (
        <div className="flex justify-start">
          <div className="flex flex-col">
            <span className="text-text2 text-sm font-semibold">{row.original.attemptCount} / {
            row.original?.attemptsAllowed || 0
          }</span>
          {(row.original?.attemptsRequired ?? 0) > 0 && (
            <span className="text-gray-601 text-xs">{row.original?.attemptsRequired} required</span>
          )}
          </div>
        </div>
        );
      },
    },
    ...(showOverallSummary ? [{
      accessorKey: "workflowStatus",
      header: ({ column }) => {
        return <div className="p-0 min-w-[175px] flex justify-center">Workflow progress</div>;
      },
      cell: ({ row }) => {
        return <div className="flex justify-center p-0 min-w-[175px]"><WorkflowStatusCell className="!justify-center" status={row.original.workflowStatus} certificationAssets={certificationAssets} /></div>;
      },
    }] : []),
    {
      accessorKey: "evaluationStatus",
      header: ({ column }) => {
        if (!enableStatusSorting) {
          return <div className="p-0">{getLabel("status.singular", labels)}</div>;
        }
        const isSorted = column.getIsSorted();
        return (
          <div
            role="button"
            tabIndex={0}
            className="p-0 flex items-center gap-2 cursor-pointer select-none hover:opacity-80"
            onClick={column.getToggleSortingHandler()}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                column.getToggleSortingHandler()?.(e);
              }
            }}
          >
            <span>{getLabel("status.singular", labels)}</span>
            <FilterIcon
              className={twMerge(
                "w-6 h-6 transition-transform",
                isSorted === "asc" && "rotate-180"
              )}
            />
          </div>
        );
      },
      cell: ({ row }) => {
        const roundedClass = showOverallSummary ? "!rounded-full" : "";
        return <StatusCell status={row.original.evaluationStatus} certificationAssets={certificationAssets} className={roundedClass} />;
      },
      enableSorting: enableStatusSorting,
    },
    {
      accessorKey: "Start Date",
      header: ({ column }) => {
        return <div className="text-center">Start Date</div>;
      },
      cell: ({ row }) => {
        return (
          <div className="text-center">
            {dayjs(row.original.certificationStartDate).utc().format("MMM DD, YYYY")}
          </div>
        );
      },
    },

    {
      id: "actions",
      header: () => <div />,
      cell: ({ row }) => (
        <div className="w-full flex justify-end pr-2">
          <ActionMenu row={row.original} onStart={onStart} onView={onView} />
        </div>
      ),
    },
  ];
  return [
    {
      accessorKey: "certificationName",
      header: ({ column }) => {
        return <div className="p-0 min-w-[200px]">{getLabel("session.plural", labels)}</div>;
      },
      cell: ({ row }) => {
        return <div>{row.original?.certificationName}</div>;
      },
    },
    ...commonColumns,
  ];
};

type User = {
  id: string;
  name: string;
  email: string;
  joinedOn: string;
  tier: string;
  totalSellers: number;
  status: string;
  statusText: string;
  statusDescription: string;
  totalSpend: string;
  stripeCustomerId: string;
  subscription: any;
  openDrawer: (row: any) => void;
  deleteModal: (row: any) => void;
  deactivateModal: (row: any) => void;
};

export function CertificationTable({
  type,
  userId,
  filter,
  certifications,
  certificationList,
  isLoading,
  users,
  usersLoading,
  setSelectedCertificationAssignment,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
}: {
  type: "manager-dashboard" | "manager-user-table" | "user-dashboard" | "admin-dashboard";
  userId?: string;
  filter?: any;
  certifications: any;
  certificationList: any;
  isLoading: boolean;
  users: any[];
  usersLoading?: boolean;
  setSelectedCertificationAssignment: (row: any) => void;
  fetchNextPage?: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
}) {
  const navigate = useNavigate();
  const { certificationAssets, tenantProfile } = useApp();
  const routeNames = tenantProfile?.routeNames ?? {};
  const url = getUrl("certificationRoute", routeNames);
  const [sorting, setSorting] = useState<SortingState>([]);
  // Same constraint as `filter.sortBy` in filterData: client-side sorting only sees
  // the pages fetched so far, so it contradicts the server cursor order and makes
  // rows jump as later pages arrive. Keep column sorting off for paginated views.
  const isCursorPaginated = !!fetchNextPage;
  const sentinelRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!fetchNextPage || !sentinelRef.current || !scrollContainerRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      {
        root: scrollContainerRef.current,
        rootMargin: "200px",
        threshold: 0.1,
      }
    );

    observer.observe(sentinelRef.current);

    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const data = useMemo(() => {
    if (Array.isArray(certifications)) {
      return certifications;
    }
    return certifications?.data?.data || [];
  }, [certifications, certificationList]);

  const showOverallSummary = isCertificationOverallSummaryEnabled();
  const columns: ColumnDef<User>[] = getColumns({
    onStart: (row) => {
      navigate(`/${url}/simulation?assignmentId=${row.id}&minimumAttempts=${row.attemptsRequired ?? 1}`);
    },
    onView: (row) => {
      navigate(buildReviewUrl(url, row.id));
    },
    showOverallSummary,
    enableStatusSorting: !isCursorPaginated,
  }, certificationAssets);

  return (
    <div>
      <div className="flex flex-col gap-5 rounded-md p-1">
        <DataTable
          columns={columns}
          onRowClick={(row) => {
            // setSelectedRow(row);
          }}
          data={filterData(data, filter, type)}
          isLoading={isLoading || !!usersLoading}
          sorting={isCursorPaginated ? undefined : sorting}
          setSorting={isCursorPaginated ? undefined : setSorting}
          loadingSkeleton={<LoadingSkeleton />}
          getRowId={isCursorPaginated ? (row) => row.id : undefined}
          scrollContainerRef={isCursorPaginated ? scrollContainerRef : undefined}
          footerContent={
            isCursorPaginated ? (
              <>
                {hasNextPage && (
                  <div ref={sentinelRef} className="h-px w-full" />
                )}
                {isFetchingNextPage && (
                  <div className="text-center py-2 bg-white">
                    <span className="text-text-gray text-sm">Loading more...</span>
                  </div>
                )}
              </>
            ) : undefined
          }
        />
      </div>
    </div>
  );
}
